import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { Menu, LogOut } from "lucide-react";

function NavItems({ items, onClick }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onClick}
          data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
                : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
            }`
          }
        >
          <it.icon className="h-4.5 w-4.5 h-5 w-5" />
          <span>{it.label}</span>
          {it.badge ? (
            <span className="ml-auto rounded-full bg-amber-500/20 text-amber-300 px-2 py-0.5 text-xs font-mono">{it.badge}</span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}

export function Shell({ items, tag }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    nav("/");
  };

  const SidebarInner = ({ onNav }) => (
    <div className="flex h-full flex-col">
      <div className="px-2 py-1">
        <Brand size="md" />
        <div className="mt-1 ml-1 text-xs font-mono uppercase tracking-widest text-emerald-500/70">{tag}</div>
      </div>
      <div className="mt-7 flex-1">
        <NavItems items={items} onClick={onNav} />
      </div>
      <div className="mt-4 rounded-xl border border-white/5 bg-black/20 p-3">
        <div className="text-sm font-semibold truncate">{user?.name}</div>
        <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        <Button onClick={doLogout} variant="outline" size="sm" data-testid="logout-btn"
          className="mt-3 w-full border-slate-700 bg-transparent hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/30">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen vp-grid-bg">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col border-r border-white/5 bg-[#0b111c]/80 backdrop-blur-xl p-4">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-white/5 bg-[#0b111c]/90 backdrop-blur-xl px-4 py-3">
        <Brand size="sm" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-slate-300" data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 bg-[#0b111c] border-white/10 p-4">
            <SidebarInner onNav={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      <main className="lg:pl-72">
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, sub, accent = "emerald", testid }) {
  const colors = {
    emerald: "from-emerald-500/15 text-emerald-400 border-emerald-500/20",
    cyan: "from-cyan-500/15 text-cyan-400 border-cyan-500/20",
    amber: "from-amber-500/15 text-amber-400 border-amber-500/20",
    rose: "from-rose-500/15 text-rose-400 border-rose-500/20",
    slate: "from-slate-500/15 text-slate-300 border-slate-500/20",
  };
  return (
    <div className="vp-card rounded-xl p-5 vp-fade" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</div>
          <div className="mt-2 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{value}</div>
          {sub ? <div className="mt-1 text-xs text-slate-500 font-mono">{sub}</div> : null}
        </div>
        {Icon ? (
          <div className={`h-10 w-10 grid place-items-center rounded-lg bg-gradient-to-br to-transparent border ${colors[accent]}`}>
            <Icon className="h-5 w-5" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function PageHeader({ title, desc, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
        {desc ? <p className="mt-1 text-sm text-slate-400">{desc}</p> : null}
      </div>
      {children}
    </div>
  );
}
